import { useCallback, useMemo } from 'react';
import { Dropdown, Option, OptionGroup, SelectionEvents, OptionOnSelectData } from '@fluentui/react-components';

import { TUpdateUser, TUser, USER_GROUPS, USER_GROUP_NAMES } from '../types/users.types';
import { TOrganization } from '../types/organizations.types';
import { EMPTY_CUSTOM_CLUSTER_ENABLED } from '../constants';

type TProps = {
  user: TUser;
  org: TOrganization;
  className?: string;
  onChange: (update: TUpdateUser) => void;
};

const UserGroupsDropdown = ({ user, org, className, onChange }: TProps) => {
  const selectedOptions = useMemo(() => [...user.groups, ...user.customGroups], [user.groups, user.customGroups]);

  const hasCustomGroups = org.customGroups.length > 0;
  const customGroupNameById = useMemo(() => new Map(org.customGroups.map((g) => [g.id, g.name])), [org.customGroups]);

  const displayValue = useMemo(() => {
    const names: string[] = [];
    // built-in names
    for (const g of user.groups) {
      const n = USER_GROUP_NAMES[g as USER_GROUPS];
      if (n) names.push(n);
    }
    // custom names
    for (const id of user.customGroups) {
      const n = customGroupNameById.get(id);
      if (n) names.push(n);
    }
    const head = names.slice(0, 2).join(', ');
    return head + (names.length > 2 ? ', ...' : '');
  }, [user.groups, user.customGroups, customGroupNameById]);

  const onOptionSelect = useCallback((_e: SelectionEvents, data: OptionOnSelectData) => {
    const isBuiltin = (val: string) => Object.prototype.hasOwnProperty.call(USER_GROUP_NAMES, val);
    const groups = data.selectedOptions.filter(isBuiltin);
    const customGroups = data.selectedOptions.filter((v) => !isBuiltin(v));
    onChange({ groups, customGroups });
  }, []);

  // --- Rendering
  // If no custom groups → single flat list for cleaner UI
  if (!hasCustomGroups && !EMPTY_CUSTOM_CLUSTER_ENABLED) {
    return (
      <Dropdown
        multiselect
        appearance="underline"
        selectedOptions={selectedOptions}
        value={displayValue}
        className={className}
        onOptionSelect={onOptionSelect}
      >
        {Object.keys(USER_GROUP_NAMES).map((key) => (
          <Option key={key} value={key}>
            {USER_GROUP_NAMES[key as USER_GROUPS]}
          </Option>
        ))}
      </Dropdown>
    );
  }

  // Otherwise: clustered (and optionally show a disabled placeholder when empty)
  return (
    <Dropdown
      multiselect
      appearance="underline"
      selectedOptions={selectedOptions}
      value={displayValue}
      className={className}
      onOptionSelect={onOptionSelect}
    >
      <OptionGroup label="Default groups">
        {Object.keys(USER_GROUP_NAMES).map((key) => (
          <Option key={key} value={key}>
            {USER_GROUP_NAMES[key as USER_GROUPS]}
          </Option>
        ))}
      </OptionGroup>

      <OptionGroup label="Custom groups">
        {hasCustomGroups ? (
          org.customGroups.map((g) => (
            <Option key={g.id} value={g.id}>
              {g.name}
            </Option>
          ))
        ) : (
          // Reachable only when EMPTY_CUSTOM_CLUSTER_ENABLED is true; the flat-list branch above handles the common case.
          <Option value="__no_custom_groups" disabled>
            No custom groups yet
          </Option>
        )}
      </OptionGroup>
    </Dropdown>
  );
};

export default UserGroupsDropdown;
